import React, { useState, useEffect, useRef } from "react";
import { CSVLink } from "react-csv";
import { connect } from "react-redux";
import classnames123 from "classnames";
import { Nav, NavItem, NavLink, TabPane, TabContent } from "reactstrap";

import {
  tabsName,
  emptyString,
  starRatingConstants,
  starRatingExportHeaders,
  conversionRateExportHeaders,
  errorMessages,
  toastTypes,
  inboxConversationExportHeaders,
} from "../../constants";
import {
  fileName,
  calculateChange,
  escapeSearchQuery,
  convertToDateToUnix,
  updatedColumnHeaders,
  convertFromDateToUnix,
  datePreferenceUtility,
  participationPaginatedPostRequestBody,
  guestSentimentPaginatedPostRequestBody,
  customToast,
  handleAsyncAction,
} from "../../../../shared";
import { performanceActions } from "../../redux/actions";
import Filter from "./filter";
import StarRatingTab from "./tabs/starRatingTab";
import ConversionRateTab from "./tabs/conversionRateTab";
import InboxConversationTab from "./tabs/inboxConversationTab";
import { ENV } from "../../../../config/env";

//Extract required properties using destructuring
const { minStarRatingVal, maxStarRatingVal } = starRatingConstants;

const componentName = "Brand Performance";

const Performance = (props) => {
  //You can Extract any data OR Function that this component receive as PROPS
  const {
    frRoute,
    resetAllExportItems,
    getAllBrandsResponses,
    getInboxConversationData,
    resetConversionRateReducer,
    getInboxConversationSummary,
    getAllBrandsConversionResponses,
    getSurveyConversionRateKpiSummary,
    getInboxConversationDataForExport,
    getAllBrandResponsesItemsForExport,
    getAllBrandsConversionResponsesForExport,
    performanceReducer: {
      allItemsForExport,
      brandResponsesData,
      brandConversionData,
      allBrandResponsesData,
      brandResponseDataPagination,
      brandConversionDataPagination,
      surveyConversionRateKpiSummary,
      inboxConversationDataPagination,
    },
  } = props;

  const defaultDate = {
    ...datePreferenceUtility(),
  };

  //Initialize states
  const csvLinkRef = useRef();
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [minStar, setMinStar] = useState([minStarRatingVal]);
  const [maxStar, setMaxStar] = useState([maxStarRatingVal]);
  const [activeTab, setActiveTab] = useState(tabsName.starRating);
  const [sorting, setSorting] = useState({ id: "name", desc: true });
  const [sortingStarRating, setSortingStarRating] = useState({
    id: "recordCount",
    desc: true,
  });
  const [sortingInboxTab, setSortingInboxTab] = useState({
    id: "responseRate",
    desc: true,
  });
  const [searchString, setSearchString] = useState(emptyString);
  const [searchStringStarRating, setSearchStringStarRating] =
    useState(emptyString);
  const [inboxTabSearch, setInboxTabSearch] = useState(emptyString);
  const [searchStringConversionRate, setSearchStringConversionRate] =
    useState(emptyString);
  const [selectedOption, setSelectedOption] = useState({
    starRating: [],
  });
  const [dayCounts, setDayCounts] = useState(emptyString);
  const [dateState, setDateState] = useState(defaultDate);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedInboxFilter, setSelectedInboxFilter] = useState({
    name: "HungerRush",
    value: 1,
  });
  const [conditionValue, setConditionValue] = useState({
    fromValue: 0,
    toValue: 100,
  });

  const createDateFrom = convertFromDateToUnix(dateState.startDate);
  const createDateTo = convertToDateToUnix(dateState.endDate);
  const conversionRateStartDate = Number.parseInt(
    ENV.CONVERSION_RATE_START_DATE_IN_UTC?.trim(),
    10
  );

  const toggleTab = (tab) => {
    setActiveTab(tab);

    if (searchString !== emptyString) {
      setSearchString(emptyString);
    }

    if (searchStringStarRating !== emptyString) {
      setSearchStringStarRating(emptyString);
    }
    if (searchStringConversionRate !== emptyString) {
      setSearchStringConversionRate(emptyString);
    }
    if (selectedFilter !== null) {
      setSelectedFilter(null);
    }
  };

  const getPaginatedConversionRatePostRequestBody = (
    pageNumber,
    sortBy,
    size
  ) => {
    const requestBody = participationPaginatedPostRequestBody(
      brandConversionDataPagination,
      pageNumber,
      searchStringConversionRate,
      sortBy || sorting,
      emptyString,
      emptyString,
      size
    );
    if (sortBy) {
      setSorting(sortBy);
    }
    return requestBody;
  };

  //Check valid start date for Conversion Rate
  const startDateForConversionRate =
    createDateFrom < conversionRateStartDate
      ? conversionRateStartDate
      : createDateFrom;

  //Utility function to check date range and show appropriate toast messages
  const checkDateRange = () => {
    if (
      createDateTo < conversionRateStartDate &&
      createDateFrom < conversionRateStartDate
    ) {
      customToast(
        errorMessages.NO_DATA_FOUND_IN_SELECTED_DATE_RANGE,
        toastTypes.ERROR
      );
      resetConversionRateReducer();
      return false; //Return false to indicate no further processing is needed
    } else if (
      createDateTo > conversionRateStartDate &&
      createDateFrom < conversionRateStartDate
    ) {
      customToast(
        `Data is only available from ${ENV.CONVERSION_RATE_START_DATE}`,
        toastTypes.INFO,
        4000 //Wait for 4 seconds
      );
    }
    return true; //Return true to indicate the range is valid for further processing
  };

  const getConversionRateData = (pageNumber, sortBy, size, canGetSummary) => {
    //If Start and End Date is before conversationRateStartDate then display no data found & Return
    const isValidDateRange = checkDateRange();

    //Check if the date range is not valid then return & don't hit API
    if (!isValidDateRange) return;

    const requestBody = getPaginatedConversionRatePostRequestBody(
      pageNumber,
      sortBy,
      size
    );

    // Always fetch conversion responses
    getAllBrandsConversionResponses(
      startDateForConversionRate,
      createDateTo,
      requestBody
    );

    // Get summary only when required
    if (canGetSummary) {
      getSurveyConversionRateKpiSummary(
        startDateForConversionRate,
        createDateTo,
        requestBody
      );
    }
  };

  const getInboxConversationAllData = (pageNumber, size, isGetSummary) => {
    const body = {
      pageNumber:
        pageNumber >= 0 ? pageNumber : inboxConversationDataPagination.number,
      length: size || inboxConversationDataPagination.size,
      search: escapeSearchQuery(inboxTabSearch),
    };

    //Fetch inbox conversation data
    getInboxConversationData(
      createDateFrom,
      createDateTo,
      selectedInboxFilter.value,
      body
    );

    //Get summary only when required
    if (isGetSummary) {
      getInboxConversationSummary(
        createDateFrom,
        createDateTo,
        selectedInboxFilter.value
      );
    }
  };

  const refreshConversionRate = () => {
    getConversionRateData(0, null, brandConversionDataPagination.size, true);
  };

  const refreshInboxConversation = () => {
    getInboxConversationAllData(0, inboxConversationDataPagination.size, true);
  };

  //Used to export CSV
  const exportCSV = () => {
    setTimeout(() => {
      csvLinkRef.current?.link.click();
    }, 1000);
  };

  //Get all data for export
  const getExportData = () => {
    let headers;
    let requestBody;

    if (activeTab === tabsName.starRating) {
      headers = starRatingExportHeaders;
      const averageSentiments = {
        min: minStar,
        max: maxStar,
      };
      requestBody = guestSentimentPaginatedPostRequestBody({
        pagination: {
          size: brandResponseDataPagination.size,
        },
        size: brandResponseDataPagination.size,
        pageNumber: brandResponseDataPagination.number,
        sortBy: sortingStarRating,
        filterQuery: emptyString,
        averageSentiments,
        isFrRoute: frRoute,
      });
      getAllBrandResponsesItemsForExport(createDateFrom, createDateTo, {
        ...requestBody,
        search: escapeSearchQuery(searchStringStarRating),
      });
    }
    if (activeTab === tabsName.conversionRate) {
      const isValidDateRange = checkDateRange();

      //Check if the date range is not valid then return & don't hit API
      if (!isValidDateRange) return;

      headers = conversionRateExportHeaders;
      requestBody = participationPaginatedPostRequestBody(
        {
          size: brandConversionDataPagination.size,
        },
        brandConversionDataPagination.number,
        searchString,
        sorting,
        emptyString,
        emptyString,
        brandConversionDataPagination.size
      );
      getAllBrandsConversionResponsesForExport(
        startDateForConversionRate,
        createDateTo,
        requestBody
      );
    }
    if (activeTab === tabsName.inboxConversationTab) {
      headers = inboxConversationExportHeaders;
      const body = {
        pageNumber: inboxConversationDataPagination.number,
        length: inboxConversationDataPagination.size,
        search: escapeSearchQuery(inboxTabSearch),
      };

      handleAsyncAction(
        getInboxConversationDataForExport(
          createDateFrom,
          createDateTo,
          selectedInboxFilter?.value,
          body,
        ),
        {
          onSuccess: (data) => {
            setExportData(data);
            exportCSV();
          },
        },
      );
    }

    setExportHeaders(updatedColumnHeaders(headers, dayCounts));
  };

  const updatedRecordStarRating = (items) => {
    return items.map((item) => {
      return {
        ...item,
        averageGuestRatings: item.averageGuestRatings,
        previousGuestRatings: item.previousGuestRatings,
        change: calculateChange(
          item.averageGuestRatings,
          item.previousGuestRatings
        ),
      };
    });
  };

  const formatExportItems = (allItemsForExport) => {
    return allItemsForExport.map((item) => ({
      brandId: item.brandId,
      name: item.name,
      surveySentValue: item.surveySentCount.value,
      surveySentPercentage: item.surveySentCount.percentage,
      surveyOpenedValue: item.surveyOpenedCount.value,
      surveyOpenedPercentage: item.surveyOpenedCount.percentage,
      surveyStartedValue: item.surveyStartedCount.value,
      surveyStartedPercentage: item.surveyStartedCount.percentage,
      surveyCompleteValue: item.surveyCompleteCount.value,
      surveyCompletePercentage: item.surveyCompleteCount.percentage,
      positiveSurveyCompleteValue: item.positiveSurveyCompleteCount.value,
      positiveSurveyCompletePercentage:
        item.positiveSurveyCompleteCount.percentage,
      passiveSurveyCompleteValue: item.passiveSurveyCompleteCount.value,
      passiveSurveyCompletePercentage:
        item.passiveSurveyCompleteCount.percentage,
      negativeSurveyCompleteValue: item.negativeSurveyCompleteCount.value,
      negativeSurveyCompletePercentage:
        item.negativeSurveyCompleteCount.percentage,
      googleReviewValue: item.googleReviewCount.value,
      googleReviewPercentage: item.googleReviewCount.percentage,
    }));
  };

  useEffect(() => {
    if (allItemsForExport.length) {
      if (activeTab === tabsName.starRating) {
        setExportData(updatedRecordStarRating(allItemsForExport));
      } else if (activeTab === tabsName.conversionRate) {
        setExportData(formatExportItems(allItemsForExport));
      }
      exportCSV();
      resetAllExportItems();
    }
    // eslint-disable-next-line
  }, [allItemsForExport]);

  return (
    <>
      <div className="container-fluid pt-2 mt-4">
        <div className="notification-form shadow card px-4 mb-3">
          <div className="card-body py-2 px-0">
            <div className="tabs tabs--bordered-bottom">
              <Nav tabs className="mb-2">
                <NavItem className="cursor-pointer nav-pill-item">
                  <NavLink
                    className={classnames123({
                      active: activeTab === tabsName.starRating,
                    })}
                    onClick={() => toggleTab(tabsName.starRating)}
                  >
                    {tabsName.starRating}
                  </NavLink>
                </NavItem>
                <NavItem className="cursor-pointer nav-pill-item">
                  <NavLink
                    className={classnames123({
                      active: activeTab === tabsName.conversionRate,
                    })}
                    onClick={() => toggleTab(tabsName.conversionRate)}
                  >
                    {tabsName.conversionRate}
                  </NavLink>
                </NavItem>
                <NavItem className="cursor-pointer nav-pill-item">
                  <NavLink
                    className={classnames123({
                      active: activeTab === tabsName.inboxConversationTab,
                    })}
                    onClick={() => toggleTab(tabsName.inboxConversationTab)}
                  >
                    {tabsName.inboxConversationTab}
                  </NavLink>
                </NavItem>
              </Nav>
              <TabContent
                activeTab={activeTab}
                className="tab-content-diagnosis"
              >
                <TabPane tabId={tabsName.starRating}>
                  <Filter
                    activeTab={activeTab}
                    setMaxStar={setMaxStar}
                    setMinStar={setMinStar}
                    dateState={dateState}
                    setDateState={setDateState}
                    setDayCounts={setDayCounts}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                  />
                </TabPane>
                <TabPane tabId={tabsName.conversionRate}>
                  <Filter
                    activeTab={activeTab}
                    dateState={dateState}
                    setDateState={setDateState}
                    setDayCounts={setDayCounts}
                    selectedOption={selectedOption}
                    selectedFilter={selectedFilter}
                    conditionValue={conditionValue}
                    setSelectedFilter={setSelectedFilter}
                    setConditionValue={setConditionValue}
                    setSelectedOption={setSelectedOption}
                    onRefreshButtonClick={refreshConversionRate}
                  />
                </TabPane>
                <TabPane tabId={tabsName.inboxConversationTab}>
                  <Filter
                    activeTab={activeTab}
                    dateState={dateState}
                    setDateState={setDateState}
                    setDayCounts={setDayCounts}
                    selectedOption={selectedOption}
                    conditionValue={conditionValue}
                    selectedFilter={selectedInboxFilter}
                    setConditionValue={setConditionValue}
                    setSelectedOption={setSelectedOption}
                    setSelectedFilter={setSelectedInboxFilter}
                    onRefreshButtonClick={refreshInboxConversation}
                  />
                </TabPane>
              </TabContent>
            </div>
          </div>
        </div>
      </div>
      {activeTab === tabsName.starRating ? (
        <StarRatingTab
          maxStar={maxStar}
          minStar={minStar}
          frRoute={frRoute}
          dayCounts={dayCounts}
          exportFunc={getExportData}
          createDateTo={createDateTo}
          createDateFrom={createDateFrom}
          selectedOption={selectedOption}
          sortingStarRating={sortingStarRating}
          userResponsesData={brandResponsesData}
          setSortingStarRating={setSortingStarRating}
          getAllBrandsResponses={getAllBrandsResponses}
          searchStringStarRating={searchStringStarRating}
          totalRestaurants={allBrandResponsesData.totalItems}
          setSearchStringStarRating={setSearchStringStarRating}
          totalResponses={allBrandResponsesData.totalResponses}
          userResponseDataPagination={brandResponseDataPagination}
        />
      ) : activeTab === tabsName.inboxConversationTab ? (
        <InboxConversationTab
          dayCounts={dayCounts}
          exportFunc={getExportData}
          createDateTo={createDateTo}
          createDateFrom={createDateFrom}
          inboxTabSearch={inboxTabSearch}
          sortingInboxTab={sortingInboxTab}
          getData={getInboxConversationAllData}
          setInboxTabSearch={setInboxTabSearch}
          setSortingInboxTab={setSortingInboxTab}
          selectedInboxFilter={selectedInboxFilter}
        />
      ) : (
        <ConversionRateTab
          sorting={sorting}
          dayCounts={dayCounts}
          setSorting={setSorting}
          exportFunc={getExportData}
          createDateTo={createDateTo}
          selectedFilter={selectedFilter}
          getData={getConversionRateData}
          createDateFrom={createDateFrom}
          brandConversionData={brandConversionData}
          searchStringConversionRate={searchStringConversionRate}
          setSearchStringConversionRate={setSearchStringConversionRate}
          brandConversionDataPagination={brandConversionDataPagination}
          surveyConversionRateKpiSummary={surveyConversionRateKpiSummary}
        />
      )}
      <CSVLink
        target="_blank"
        ref={csvLinkRef}
        className="hidden"
        headers={exportHeaders}
        data={exportData}
        filename={`${fileName(
          `${componentName} (${activeTab})`,
          createDateFrom,
          createDateTo
        )}.csv`}
      />
    </>
  );
};

//Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  performanceReducer: state.performanceReducer,
});

//Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getAllBrandsResponses: (createDateFrom, createDateTo, body) =>
    dispatch(
      performanceActions.getAllBrandsResponses(
        createDateFrom,
        createDateTo,
        body
      )
    ),
  getAllBrandsConversionResponses: (createDateFrom, createDateTo, body) =>
    dispatch(
      performanceActions.getAllBrandsConversionResponses(
        createDateFrom,
        createDateTo,
        body
      )
    ),
  getSurveyConversionRateKpiSummary: (createDateFrom, createDateTo, body) =>
    dispatch(
      performanceActions.getSurveyConversionRateKpiSummary(
        createDateFrom,
        createDateTo,
        body
      )
    ),
  getAllBrandsConversionResponsesForExport: (
    createDateFrom,
    createDateTo,
    body
  ) =>
    dispatch(
      performanceActions.getAllBrandsConversionResponsesForExport(
        createDateFrom,
        createDateTo,
        body
      )
    ),
  getInboxConversationData: (createDateFrom, createDateTo, platformId, body) =>
    dispatch(
      performanceActions.getInboxConversationData(
        createDateFrom,
        createDateTo,
        platformId,
        body
      )
    ),
  getInboxConversationSummary: (
    createDateFrom,
    createDateTo,
    orderPlatformType
  ) =>
    dispatch(
      performanceActions.getInboxConversationSummary(
        createDateFrom,
        createDateTo,
        orderPlatformType
      )
    ),
  getInboxConversationDataForExport: (
    createDateFrom,
    createDateTo,
    platformId,
    body,
  ) =>
    dispatch(
      performanceActions.getInboxConversationDataForExport(
        createDateFrom,
        createDateTo,
        platformId,
        body,
      ),
    ),
  getAllBrandResponsesItemsForExport: (createDateFrom, createDateTo, body) =>
    dispatch(
      performanceActions.getAllBrandResponsesItemsForExport(
        createDateFrom,
        createDateTo,
        body
      )
    ),
  resetAllExportItems: () => dispatch(performanceActions.resetAllExportItems()),
  resetConversionRateReducer: () =>
    dispatch(performanceActions.resetConversionRateReducer()),
  resetInboxReportReducer: () =>
    dispatch(performanceActions.resetInboxReportReducer()),
});

//Mapping action and store the function via props
export default connect(mapStateToProps, mapDispatchToProps)(Performance);
